/**
 * Provides APIs to work on user data.
 * @module UserApi
 */
/*
  •	Copyright (c) 2024 Pegasystems Inc.
  •	All rights reserved.
*/

import { resolveRestApi } from '../router/app-router';
import { invokeRestApi } from '../rest-client';

const GET_OPERATOR_DETAILS = 'operatorDetails';
const GET_OPERATOR_BY_BUSINESS_ID = 'operatorDetailsByBusinessID';

/**
 * This API obtains the glimpse data of a specified user.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the user glimpse data of a user whose ID is 'author@constellation.com'.</caption>
 * PCore.getUserApi().getOperatorDetails('author@constellation.com')
 * .then(response => {
 *  // The response of this API is as shown below:
 *  "data": [{
 *         "@class": "User",
 *        "ID": "Z2xvYmFsVXNlpl82M2RiZWQ2MGJlZGEzZTg2OTgyNTBiNGM",
 *        "Name": “Author Constellation”,
 *        "IsActive": true,
 *        "BusinessID": "author@constellation.com",
 *        "Email": "author@constellation.com",
 *        "AccessGroup": "globalGoMechanicDefaultAG"
 *     }],
 * "pageNumber": 1,
 * "pageSize": 1,
 * "fetchDateTime": "2023-11-13T17:28:36.185Z")
 * .catch(error => {
 *    console.log(error);
 * });
 *
 * @param userID The ID of the user whose user glimpse data must be obtained.
 * @param isBusinessID The flag which determines if the userID passed is in EmailID format or shortID format
 * @returns The Promise
 */
export const getOperatorDetails = (userID: string, isBusinessID = false): Promise<object> => {
  const { reqType } = resolveRestApi(GET_OPERATOR_DETAILS, {});
  if (reqType === 'GET') {
    const queryPayload = {
      encodedOperatorId: encodeURIComponent(`{"opname":"${userID}"}`)
    };
    return invokeRestApi(GET_OPERATOR_DETAILS, { queryPayload });
  }
  if (isBusinessID) {
    return invokeRestApi(GET_OPERATOR_BY_BUSINESS_ID, {
      body: { dataViewParameters: { BusinessID: userID } }
    });
  }
  return invokeRestApi(GET_OPERATOR_DETAILS, {
    body: { dataViewParameters: { ID: userID } }
  });
};

export default {
  getOperatorDetails
};
